import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { useLang, tr } from "../contexts/LangContext";
import { translations } from "../lib/i18n";
import { CONTENT_PAGES, IMG_DEFAULTS, getPath } from "../lib/contentSchema";
import { toast } from "sonner";
import { GripVertical, Plus, Trash2, Upload, Save, Eye, EyeOff, ChevronDown, ChevronUp, Monitor, Smartphone, RefreshCw } from "lucide-react";

const PAGE_PATHS = {
  home: "", club: "club", voile: "voile", motor: "motor", stages: "stages",
  regattas: "regattas", agenda: "agenda", gallery: "galerie", shop: "shop", contact: "contact",
};

const LANGS = ["fr", "nl", "en"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (u && u.startsWith("/uploads/") ? `${BACKEND}/api${u}` : u);

const inputCls = "w-full border border-slate-300 focus:border-navy outline-none py-2 px-3 bg-white text-sm";
const btnCls = "inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red transition-colors disabled:opacity-50";

function LangTabs({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {LANGS.map((l) => (
        <button key={l} type="button" onClick={() => onChange(l)}
          className={`px-3 py-1.5 text-[10px] uppercase tracking-widest border ${value === l ? "bg-navy text-white border-navy" : "border-slate-300 text-slate-500 hover:border-navy"}`}
          data-testid={`content-lang-${l}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

function ImageInput({ value, onChange, testid }) {
  const fileRef = useRef(null);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/content/upload-image", fd);
      onChange(r.data.url);
      toast.success(tr("Image téléversée"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur upload");
    }
  };
  return (
    <div className="flex items-center gap-2">
      {value && <img src={fullUrl(value)} alt="" className="w-14 h-10 object-cover border border-slate-200 shrink-0" />}
      <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={tr("URL de l'image")} data-testid={testid} />
      <button type="button" className="shrink-0 border border-slate-300 p-2 hover:border-navy" onClick={() => fileRef.current?.click()} title={tr("Téléverser")}>
        <Upload className="w-4 h-4" />
      </button>
      <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
    </div>
  );
}

function useDragList(items, onReorder) {
  const dragIdx = useRef(null);
  return {
    rowProps: (i) => ({
      draggable: true,
      onDragStart: () => { dragIdx.current = i; },
      onDragOver: (e) => e.preventDefault(),
      onDrop: (e) => {
        e.preventDefault();
        const from = dragIdx.current;
        if (from === null || from === i) return;
        const next = [...items];
        const [moved] = next.splice(from, 1);
        next.splice(i, 0, moved);
        dragIdx.current = null;
        onReorder(next);
      },
    }),
  };
}

function ListEditor({ def, items, onChange }) {
  const list = Array.isArray(items) ? items : [];
  const { rowProps } = useDragList(list, onChange);
  const setItem = (i, patch) => {
    const next = [...list];
    next[i] = def.simple ? patch : { ...next[i], ...patch };
    onChange(next);
  };
  const addItem = () => onChange([...list, def.simple ? "" : Object.fromEntries(def.fields.map((f) => [f.name, ""]))]);
  const removeItem = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid={`list-editor-${def.key}`}>
      {list.map((item, i) => (
        <div key={i} {...rowProps(i)} className="flex items-start gap-2 bg-sand border border-slate-200 p-3 cursor-move">
          <GripVertical className="w-4 h-4 text-slate-400 mt-2 shrink-0" />
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
            {def.simple ? (
              <input className={`${inputCls} md:col-span-2`} value={item || ""} onChange={(e) => setItem(i, e.target.value)} />
            ) : def.fields.map((f) => (
              <div key={f.name} className={f.type === "textarea" || f.type === "image" ? "md:col-span-2" : ""}>
                <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">{tr(f.label)}</label>
                {f.type === "textarea" ? (
                  <textarea rows={2} className={`${inputCls} resize-none`} value={item[f.name] || ""} onChange={(e) => setItem(i, { [f.name]: e.target.value })} />
                ) : f.type === "image" ? (
                  <ImageInput value={item[f.name]} onChange={(v) => setItem(i, { [f.name]: v })} />
                ) : (
                  <input className={inputCls} value={item[f.name] || ""} onChange={(e) => setItem(i, { [f.name]: e.target.value })} />
                )}
              </div>
            ))}
          </div>
          <button type="button" onClick={() => removeItem(i)} className="text-slate-400 hover:text-club-red mt-2" data-testid={`list-remove-${def.key}-${i}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={addItem} className="inline-flex items-center gap-1 text-xs text-navy hover:text-club-red uppercase tracking-widest" data-testid={`list-add-${def.key}`}>
        <Plus className="w-3.5 h-3.5" />{tr("Ajouter un élément")}</button>
    </div>
  );
}

/* ===================== Live preview panel ===================== */

function LivePreview({ page, lang, draft }) {
  const iframeRef = useRef(null);
  const [device, setDevice] = useState("desktop");
  const [reloadKey, setReloadKey] = useState(0);
  const src = `/${lang}/${PAGE_PATHS[page] || ""}?cmsPreview=1`;

  const postDraft = useCallback(() => {
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "rycm-preview", pages: { [page]: draft } },
        window.location.origin
      );
    } catch { /* noop */ }
  }, [page, draft]);

  useEffect(() => {
    const timer = setTimeout(postDraft, 250);
    return () => clearTimeout(timer);
  }, [postDraft]);

  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "rycm-preview-ready") postDraft();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [postDraft]);

  return (
    <div className="bg-white border border-slate-200 flex flex-col h-full" data-testid="live-preview-panel">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 bg-sand">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{tr("Aperçu en direct")} — {lang.toUpperCase()}</div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setDevice("desktop")} className={`p-1.5 border ${device === "desktop" ? "border-navy text-navy bg-white" : "border-transparent text-slate-400"}`} title={tr("Bureau")} data-testid="preview-device-desktop">
            <Monitor className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={() => setDevice("mobile")} className={`p-1.5 border ${device === "mobile" ? "border-navy text-navy bg-white" : "border-transparent text-slate-400"}`} title="Mobile" data-testid="preview-device-mobile">
            <Smartphone className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={() => setReloadKey((k) => k + 1)} className="p-1.5 text-slate-400 hover:text-navy" title="Recharger" data-testid="preview-reload">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="flex-1 bg-slate-100 flex justify-center overflow-hidden min-h-[540px]">
        <iframe
          key={`${page}-${lang}-${reloadKey}`}
          ref={iframeRef}
          title={tr("Aperçu de la page")}
          src={src}
          onLoad={postDraft}
          className={`h-full border-0 bg-white transition-all ${device === "mobile" ? "w-[390px] shadow-lg" : "w-full"}`}
          data-testid="preview-iframe"
        />
      </div>
    </div>
  );
}

/* ===================== Page content editor ===================== */

function SectionOrderList({ ids, schema, sections, onReorder, onToggle }) {
  const { rowProps } = useDragList(ids, onReorder);
  const labelOf = (id) => schema.sections.find((s) => s.id === id)?.label || id;
  return (
    <div className="space-y-2" data-testid="section-order-list">
      {ids.map((id, i) => {
        const on = sections[id] !== false;
        return (
          <div key={id} {...rowProps(i)} className="flex items-center gap-3 bg-sand border border-slate-200 px-3 py-2.5 cursor-move" data-testid={`section-row-${id}`}>
            <GripVertical className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="text-[10px] uppercase tracking-widest text-slate-400 w-6">{i + 1}.</span>
            <span className={`text-sm flex-1 ${on ? "text-navy" : "text-slate-400 line-through"}`}>{labelOf(id)}</span>
            <button type="button" onClick={() => onToggle(id)}
              className={`inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-widest border ${on ? "border-green-500 text-green-700 bg-green-50" : "border-slate-300 text-slate-400 bg-slate-50"}`}
              data-testid={`section-toggle-${id}`}>
              {on ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} {on ? tr("Visible") : tr("Masquée")}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function ContentEditor({ page }) {
  const { content, refreshContent } = useLang();
  const schema = CONTENT_PAGES[page];
  const [editLang, setEditLang] = useState("fr");
  const [texts, setTexts] = useState({ fr: {}, nl: {}, en: {} });
  const [images, setImages] = useState({});
  const [sections, setSections] = useState({});
  const [sectionIds, setSectionIds] = useState([]);
  const [seoState, setSeoState] = useState({});
  const [options, setOptions] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    if (dirty) return;
    const doc = content[page] || {};
    const allKeys = [...schema.texts.map((f) => f.key), ...schema.lists.map((l) => l.key)];
    const nextTexts = {};
    LANGS.forEach((l) => {
      nextTexts[l] = {};
      allKeys.forEach((k) => {
        const ov = doc.texts?.[l]?.[k];
        nextTexts[l][k] = ov !== undefined && ov !== "" ? ov : getPath(translations[l], k) ?? "";
      });
    });
    setTexts(nextTexts);
    const nextImages = {};
    schema.images.forEach((im) => {
      nextImages[im.key] = doc.images?.[im.key] || IMG_DEFAULTS[`${page}.${im.key}`] || "";
    });
    setImages(nextImages);
    const nextSections = {};
    schema.sections.forEach((s) => { nextSections[s.id] = doc.sections?.[s.id] !== false; });
    setSections(nextSections);
    const defIds = schema.sections.map((s) => s.id);
    const saved = doc.options?.section_order || [];
    setSectionIds([...saved.filter((x) => defIds.includes(x)), ...defIds.filter((x) => !saved.includes(x))]);
    setSeoState(doc.seo || {});
    const nextOptions = {};
    (schema.options || []).forEach((o) => { nextOptions[o.key] = doc.options?.[o.key] ?? o.def; });
    setOptions(nextOptions);
  }, [page, content, dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const setText = (key, val) => { setDirty(true); setTexts((p) => ({ ...p, [editLang]: { ...p[editLang], [key]: val } })); };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/content/${page}`, {
        texts, images, sections, seo: seoState,
        options: { ...options, ...(schema.sections.length ? { section_order: sectionIds } : {}) },
      });
      toast.success(tr("Contenu enregistré"));
      setDirty(false);
      refreshContent();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur de sauvegarde"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`grid grid-cols-1 gap-6 ${showPreview ? "xl:grid-cols-2" : ""}`}>
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <LangTabs value={editLang} onChange={setEditLang} />
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setShowPreview(!showPreview)}
            className={`hidden xl:inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.2em] border transition-colors ${showPreview ? "border-navy text-navy" : "border-slate-300 text-slate-400 hover:border-navy hover:text-navy"}`}
            data-testid="preview-toggle-btn">
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />} {tr("Aperçu")}
          </button>
          <button onClick={save} disabled={saving} className={btnCls} data-testid="content-save-btn">
            <Save className="w-4 h-4" /> {saving ? "…" : tr("Enregistrer")}
          </button>
        </div>
      </div>

      {/* Texts */}
      <div className="bg-white border border-slate-200 p-6">
        <h3 className="font-serif text-xl text-navy mb-4">{tr("Textes")} ({editLang.toUpperCase()})</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {schema.texts.map((f) => (
            <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">{tr(f.label)}</label>
              {f.type === "textarea" ? (
                <textarea rows={3} className={`${inputCls} resize-none`} value={texts[editLang][f.key] || ""} onChange={(e) => setText(f.key, e.target.value)} data-testid={`content-text-${f.key}`} />
              ) : (
                <input className={inputCls} value={texts[editLang][f.key] || ""} onChange={(e) => setText(f.key, e.target.value)} data-testid={`content-text-${f.key}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Lists */}
      {schema.lists.map((def) => (
        <div key={def.key} className="bg-white border border-slate-200 p-6">
          <h3 className="font-serif text-xl text-navy mb-1">{tr(def.label)} ({editLang.toUpperCase()})</h3>
          <p className="text-xs text-slate-400 mb-4">{tr("Glissez-déposez pour réordonner.")}</p>
          <ListEditor def={def} items={texts[editLang][def.key]} onChange={(v) => setText(def.key, v)} />
        </div>
      ))}

      {/* Images */}
      {schema.images.length > 0 && (
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="font-serif text-xl text-navy mb-4">{tr("Images")}</h3>
          <div className="space-y-4">
            {schema.images.map((im) => (
              <div key={im.key}>
                <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">{im.label}</label>
                <ImageInput value={images[im.key]} onChange={(v) => { setDirty(true); setImages((p) => ({ ...p, [im.key]: v })); }} testid={`content-image-${im.key}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sections: visibility + order */}
      {schema.sections.length > 0 && (
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="font-serif text-xl text-navy mb-1">{tr("Sections de la page")}</h3>
          <p className="text-xs text-slate-400 mb-4">{tr("Glissez-déposez pour réordonner les sections. L'œil affiche/masque.")}</p>
          <SectionOrderList
            ids={sectionIds}
            schema={schema}
            sections={sections}
            onReorder={(next) => { setDirty(true); setSectionIds(next); }}
            onToggle={(id) => { setDirty(true); setSections((p) => ({ ...p, [id]: !(p[id] !== false) })); }}
          />
        </div>
      )}

      {/* Options */}
      {(schema.options || []).length > 0 && (
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="font-serif text-xl text-navy mb-4">Options</h3>
          <div className="space-y-3">
            {schema.options.map((o) => (
              <div key={o.key}>
                <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">{o.label}</label>
                <input type={o.type || "text"} className={`${inputCls} md:w-48`} value={options[o.key] ?? ""}
                  onChange={(e) => { setDirty(true); setOptions((p) => ({ ...p, [o.key]: o.type === "number" ? (e.target.value === "" ? "" : +e.target.value) : e.target.value })); }}
                  data-testid={`content-option-${o.key}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SEO */}
      <div className="bg-white border border-slate-200 p-6">
        <h3 className="font-serif text-xl text-navy mb-4">SEO ({editLang.toUpperCase()})</h3>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">Meta title</label>
            <input className={inputCls} value={seoState[`title_${editLang}`] || ""} onChange={(e) => { setDirty(true); setSeoState((p) => ({ ...p, [`title_${editLang}`]: e.target.value })); }} data-testid="seo-title-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">Meta description</label>
            <textarea rows={2} className={`${inputCls} resize-none`} value={seoState[`desc_${editLang}`] || ""} onChange={(e) => { setDirty(true); setSeoState((p) => ({ ...p, [`desc_${editLang}`]: e.target.value })); }} data-testid="seo-desc-input" />
          </div>
        </div>
      </div>
    </div>

    {showPreview && (
      <div className="hidden xl:block sticky top-24 self-start h-[calc(100vh-8rem)]">
        <LivePreview page={page} lang={editLang} draft={{ texts, images, sections, seo: seoState, options: { ...options, ...(schema.sections.length ? { section_order: sectionIds } : {}) } }} />
      </div>
    )}
    </div>
  );
}

/* ===================== FAQ manager ===================== */

function FaqManager() {
  const [faqs, setFaqs] = useState([]);
  const [editLang, setEditLang] = useState("fr");
  const load = useCallback(() => { api.get("/admin/faqs").then((r) => setFaqs(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const { rowProps } = useDragList(faqs, async (next) => {
    setFaqs(next);
    await api.post("/admin/faqs/reorder", { ids: next.map((f) => f.id) });
    toast.success(tr("Ordre FAQ mis à jour"));
  });

  const add = async () => {
    await api.post("/admin/faqs", { question_fr: "Nouvelle question", answer_fr: "" });
    load();
  };
  const patchLocal = (id, patch) => setFaqs((p) => p.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const save = async (f) => {
    const { id, order, created_at, ...body } = f;
    await api.patch(`/admin/faqs/${id}`, body);
    toast.success(tr("FAQ enregistrée"));
    load();
  };
  const remove = async (id) => {
    if (!window.confirm(tr("Supprimer cette question ?"))) return;
    await api.delete(`/admin/faqs/${id}`);
    load();
  };

  return (
    <div className="bg-white border border-slate-200 p-6 mt-8" data-testid="faq-manager">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-serif text-xl text-navy">FAQ — page Contact</h3>
        <div className="flex items-center gap-3">
          <LangTabs value={editLang} onChange={setEditLang} />
          <button onClick={add} className={btnCls} data-testid="faq-add-btn"><Plus className="w-4 h-4" />{tr("Ajouter")}</button>
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-4">{tr("Glissez-déposez pour réordonner. L'œil masque/affiche la question sur le site.")}</p>
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <div key={f.id} {...rowProps(i)} className="border border-slate-200 bg-sand p-4 cursor-move" data-testid={`faq-item-${f.id}`}>
            <div className="flex items-start gap-2">
              <GripVertical className="w-4 h-4 text-slate-400 mt-2 shrink-0" />
              <div className="flex-1 space-y-2">
                <input className={inputCls} placeholder={`Question (${editLang.toUpperCase()})`} value={f[`question_${editLang}`] || ""} onChange={(e) => patchLocal(f.id, { [`question_${editLang}`]: e.target.value })} data-testid={`faq-question-${f.id}`} />
                <textarea rows={2} className={`${inputCls} resize-none`} placeholder={`Réponse (${editLang.toUpperCase()})`} value={f[`answer_${editLang}`] || ""} onChange={(e) => patchLocal(f.id, { [`answer_${editLang}`]: e.target.value })} data-testid={`faq-answer-${f.id}`} />
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button onClick={() => save(f)} className="text-navy hover:text-club-red" title={tr("Enregistrer")} data-testid={`faq-save-${f.id}`}><Save className="w-4 h-4" /></button>
                <button onClick={async () => { await api.patch(`/admin/faqs/${f.id}`, { visible: !(f.visible !== false) }); load(); }} className={f.visible !== false ? "text-green-600" : "text-slate-400"} title={tr("Visibilité")} data-testid={`faq-visible-${f.id}`}>
                  {f.visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button onClick={() => remove(f.id)} className="text-slate-400 hover:text-club-red" title={tr("Supprimer")} data-testid={`faq-delete-${f.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {faqs.length === 0 && <div className="text-sm text-slate-400 italic">{tr("Aucune question. Cliquez sur « Ajouter ».")}</div>}
      </div>
    </div>
  );
}

/* ===================== Stage categories manager ===================== */

function StageCategoriesManager({ onChanged }) {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ slug: "", name_fr: "", name_nl: "", name_en: "" });
  const load = useCallback(() => { api.get("/admin/stage-categories").then((r) => setCats(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const { rowProps } = useDragList(cats, async (next) => {
    setCats(next);
    for (let i = 0; i < next.length; i++) await api.patch(`/admin/stage-categories/${next[i].id}`, { order: i });
    toast.success(tr("Ordre mis à jour"));
    onChanged?.();
  });

  const add = async () => {
    try {
      await api.post("/admin/stage-categories", form);
      setForm({ slug: "", name_fr: "", name_nl: "", name_en: "" });
      toast.success(tr("Catégorie créée"));
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const save = async (c) => {
    await api.patch(`/admin/stage-categories/${c.id}`, { name_fr: c.name_fr, name_nl: c.name_nl, name_en: c.name_en, active: c.active });
    toast.success(tr("Catégorie enregistrée"));
    onChanged?.();
  };
  const remove = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.name_fr} » ?`)) return;
    try {
      await api.delete(`/admin/stage-categories/${c.id}`);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const patchLocal = (id, patch) => setCats((p) => p.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  return (
    <div className="bg-white border border-slate-200 p-6 mt-8" data-testid="stage-categories-manager">
      <h3 className="font-serif text-xl text-navy mb-1">{tr("Catégories de stages")}</h3>
      <p className="text-xs text-slate-400 mb-4">{tr("Les onglets de la page publique Stages. Glissez-déposez pour réordonner.")}</p>
      <div className="space-y-2">
        {cats.map((c, i) => (
          <div key={c.id} {...rowProps(i)} className="flex items-center gap-2 bg-sand border border-slate-200 p-3 cursor-move flex-wrap" data-testid={`stage-cat-${c.slug}`}>
            <GripVertical className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="text-[10px] uppercase tracking-widest text-slate-400 w-16 shrink-0">{c.slug}</span>
            <input className={`${inputCls} flex-1 min-w-[120px]`} value={c.name_fr || ""} onChange={(e) => patchLocal(c.id, { name_fr: e.target.value })} placeholder="FR" />
            <input className={`${inputCls} flex-1 min-w-[120px]`} value={c.name_nl || ""} onChange={(e) => patchLocal(c.id, { name_nl: e.target.value })} placeholder="NL" />
            <input className={`${inputCls} flex-1 min-w-[120px]`} value={c.name_en || ""} onChange={(e) => patchLocal(c.id, { name_en: e.target.value })} placeholder="EN" />
            <button onClick={() => patchLocal(c.id, { active: !(c.active !== false) })} className={c.active !== false ? "text-green-600" : "text-slate-400"} title={tr("Actif")}>
              {c.active !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
            <button onClick={() => save(c)} className="text-navy hover:text-club-red" title={tr("Enregistrer")} data-testid={`stage-cat-save-${c.slug}`}><Save className="w-4 h-4" /></button>
            <button onClick={() => remove(c)} className="text-slate-400 hover:text-club-red" title={tr("Supprimer")}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-4 flex-wrap">
        <input className={`${inputCls} w-28`} placeholder="slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="stage-cat-new-slug" />
        <input className={`${inputCls} flex-1 min-w-[120px]`} placeholder={tr("Nom FR")} value={form.name_fr} onChange={(e) => setForm({ ...form, name_fr: e.target.value })} data-testid="stage-cat-new-fr" />
        <input className={`${inputCls} flex-1 min-w-[120px]`} placeholder={tr("Nom NL")} value={form.name_nl} onChange={(e) => setForm({ ...form, name_nl: e.target.value })} />
        <input className={`${inputCls} flex-1 min-w-[120px]`} placeholder={tr("Nom EN")} value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} />
        <button onClick={add} className={btnCls} data-testid="stage-cat-add-btn"><Plus className="w-4 h-4" />{tr("Créer")}</button>
      </div>
    </div>
  );
}

/* ===================== Info stages manager ===================== */

const EMPTY_STAGE = { category: "", title_fr: "", title_nl: "", title_en: "", description_fr: "", description_nl: "", description_en: "", age_min: 7, age_max: 16, start_date: "", end_date: "", price: 0, places: 0, image: "" };

function StageForm({ stage, cats, onSave, onCancel }) {
  const [f, setF] = useState({ ...EMPTY_STAGE, ...stage });
  const [editLang, setEditLang] = useState("fr");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="bg-sand border border-slate-200 p-4 space-y-3" data-testid="info-stage-form">
      <div className="flex items-center justify-between">
        <LangTabs value={editLang} onChange={setEditLang} />
        <select className={`${inputCls} w-auto`} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="info-stage-category">
          <option value="">— Catégorie —</option>
          {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name_fr}</option>)}
        </select>
      </div>
      <input className={inputCls} placeholder={`${tr("Titre")} (${editLang.toUpperCase()})`} value={f[`title_${editLang}`] || ""} onChange={(e) => set(`title_${editLang}`, e.target.value)} data-testid="info-stage-title" />
      <textarea rows={2} className={`${inputCls} resize-none`} placeholder={`Description (${editLang.toUpperCase()})`} value={f[`description_${editLang}`] || ""} onChange={(e) => set(`description_${editLang}`, e.target.value)} />
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <div><label className="text-[10px] uppercase text-slate-400">Âge min</label><input type="number" className={inputCls} value={f.age_min} onChange={(e) => set("age_min", +e.target.value)} /></div>
        <div><label className="text-[10px] uppercase text-slate-400">Âge max</label><input type="number" className={inputCls} value={f.age_max} onChange={(e) => set("age_max", +e.target.value)} /></div>
        <div><label className="text-[10px] uppercase text-slate-400">{tr("Début")}</label><input type="date" className={inputCls} value={f.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
        <div><label className="text-[10px] uppercase text-slate-400">Fin</label><input type="date" className={inputCls} value={f.end_date} onChange={(e) => set("end_date", e.target.value)} /></div>
        <div><label className="text-[10px] uppercase text-slate-400">{tr("Prix €")}</label><input type="number" className={inputCls} value={f.price} onChange={(e) => set("price", +e.target.value)} /></div>
        <div><label className="text-[10px] uppercase text-slate-400">Places</label><input type="number" className={inputCls} value={f.places} onChange={(e) => set("places", +e.target.value)} /></div>
      </div>
      <ImageInput value={f.image} onChange={(v) => set("image", v)} />
      <div className="flex gap-2">
        <button onClick={() => onSave(f)} className={btnCls} data-testid="info-stage-save"><Save className="w-4 h-4" />{tr("Enregistrer")}</button>
        <button onClick={onCancel} className="text-xs uppercase tracking-widest text-slate-500 hover:text-navy px-4">{tr("Annuler")}</button>
      </div>
    </div>
  );
}

function InfoStagesManager() {
  const [stages, setStages] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/info-stages").then((r) => setStages(r.data || [])).catch(() => {});
    api.get("/admin/stage-categories").then((r) => setCats(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (f) => {
    try { await api.post("/admin/info-stages", f); toast.success(tr("Stage créé")); setAdding(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const update = async (f) => {
    const { id, places_taken, ...body } = f;
    try { await api.patch(`/admin/info-stages/${id}`, body); toast.success(tr("Stage mis à jour")); setEditing(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Supprimer « ${s.title_fr} » ?`)) return;
    await api.delete(`/admin/info-stages/${s.id}`);
    load();
  };

  return (
    <div className="bg-white border border-slate-200 p-6 mt-8" data-testid="info-stages-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-serif text-xl text-navy">{tr("Stages affichés (page publique)")}</h3>
          <p className="text-xs text-slate-400 mt-1">{tr("Fiches informatives de la page /stages, classées par catégorie.")}</p>
        </div>
        <button onClick={() => { setAdding(!adding); setEditing(null); }} className={btnCls} data-testid="info-stage-add-btn"><Plus className="w-4 h-4" /> Nouveau stage</button>
      </div>
      {adding && <div className="mb-4"><StageForm stage={EMPTY_STAGE} cats={cats} onSave={create} onCancel={() => setAdding(false)} /></div>}
      <div className="space-y-2">
        {stages.map((s) => (
          <div key={s.id} data-testid={`info-stage-${s.id}`}>
            <div className="flex items-center gap-3 border border-slate-200 p-3">
              {s.image && <img src={fullUrl(s.image)} alt="" className="w-16 h-11 object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-serif text-navy truncate">{s.title_fr}</div>
                <div className="text-[11px] text-slate-500">
                  <span className="uppercase tracking-widest text-club-red">{cats.find((c) => c.slug === s.category)?.name_fr || s.category}</span>
                  {" · "}{s.start_date} → {s.end_date} · {s.price} € · {s.places_taken || 0}/{s.places} places
                </div>
              </div>
              <button onClick={() => { setEditing(editing === s.id ? null : s.id); setAdding(false); }} className="text-navy hover:text-club-red" data-testid={`info-stage-edit-${s.id}`}>
                {editing === s.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <button onClick={() => remove(s)} className="text-slate-400 hover:text-club-red"><Trash2 className="w-4 h-4" /></button>
            </div>
            {editing === s.id && <div className="mt-1"><StageForm stage={s} cats={cats} onSave={update} onCancel={() => setEditing(null)} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ===================== Main tab ===================== */

export default function AdminContentTab() {
  const [page, setPage] = useState("home");
  return (
    <div data-testid="admin-content-tab">
      <div className="flex items-center gap-3 flex-wrap mb-6">
        <label className="text-xs uppercase tracking-widest text-slate-500">{tr("Page :")}</label>
        <select value={page} onChange={(e) => setPage(e.target.value)} className={`${inputCls} w-auto`} data-testid="content-page-select">
          {Object.entries(CONTENT_PAGES).map(([k, v]) => <option key={k} value={k}>{tr(v.label)}</option>)}
        </select>
      </div>
      <ContentEditor key={page} page={page} />
      {page === "contact" && <FaqManager />}
      {page === "stages" && (<><StageCategoriesManager /><InfoStagesManager /></>)}
    </div>
  );
}
